import { useEffect, useState, useRef } from 'react';
import { Upload } from '@b-design/fusion';
// import { Upload } from '@alifd/next';
import { getFileSize, propsFilter } from '@/utils';
import $i18n from '../../../i18n';

interface ImageUploaderProps {
  // 图片上传地址
  action?: string;
  // 支持的文件类型
  accept?: string[];
  // 上传文件大小限制，单位 byte
  maxSize?: number;
  // 宽高限制
  fileWidAndHei?: (string | number)[];
  // 子元素（自定义触发内容）
  children?: React.ReactNode;
  // 上传所需的额外参数
  data?: object;
  // 文件主体参数名
  name?: string;
  defaultValue?: any;
  // 文件列表
  value?: any[];
  // 请求的返回值格式化，默认按 oss 上传接口处理，在组件 file 的 response 中可以拿到格式化后的接口数据
  formatter?: Function;
  // 这是value 转化方法，value 转化为组件展示所需的结构，主要是为了保持出入参一致，底层 upload 的适配就交给它了
  transformer?: Function;
  // 上传前的逻辑处理
  beforeUpload?: Function;
  // 上传成功回调
  onSuccess?: Function;
  // 上传前校验报错
  onCheckFail?: (err, response, e) => void;
  // 上传报错（含限制校验和服务端错误）回调
  onError?: (err, response, e) => void;
  // 移除上传文件回调
  onRemove?: Function;
  // 文件变化回调
  onChange?: Function;
  // 禁用
  disabled?: boolean | null | '';
  // 默认icon
  defaultImg?: string;
  // 移除icon后清空value
  changeValue?: ({}) => void;
  // 是否为版本模块(版本模块需调用另外接口)
  version?: boolean;
  // 是否需要展示默认icon(入驻模块、应用设置)
  settlein?: boolean;
}

/**
 * 上传返回值格式化
 * originData 将作为 value 保存下来并提供出去
 *
 * @param {object} res 服务器端的返回值
 * @returns {object} 格式化后的文件对象
 */
export function defaultFormatter(rst) {
  const res = rst || {};
  const data = res.data || {};
  return {
    // Component Needed
    code: res.success,
    publicUrl: data.publicUrl || data.fileUrl || data.url,
    relativePath: data.relativePath || data.fileUrl || data.url,
    // Submit Needed
    success: res.success,
    errCode: res.errorCode, // TYPEERR | SIZEERR
    originData: data,
  };
}

/**
 * value 转化为组件展示所需的结构
 *
 * @param {object|array} data 服务端带出的已上传文件信息
 * @returns 符合接口标准兼容性良好的文件列表数据
 */
export function defaultTransformer(data) {
  let files;
  try {
    files = Array.isArray(data) ? data : Object.values(data);
  } catch (e) {
    files = [];
  }
  return files.map((file) => ({
    // 主要信息
    name: file.fileName || file.name,
    fileName: file.fileName || file.name,
    status: 'done',
    size: file.fileSize,
    url: file.publicUrl || file.fileUrl || file.url,
    publicUrl: file.publicUrl || file.fileUrl || file.url,
    fileURL: file.publicUrl || file.fileUrl || file.url,
    imgURL: file.publicUrl || file.fileUrl || file.url,
    // 额外信息
    mimeType: file.mimeType,
    ossKey: file.ossKey,
    suffix: file.suffix,
  }));
}

const ImageUploader = (props: ImageUploaderProps) => {
  const {
    action = '',
    accept = undefined,
    maxSize = undefined,
    fileWidAndHei = [],
    data = {},
    defaultValue = null,
    value = [],
    formatter = defaultFormatter,
    transformer = defaultTransformer,
    beforeUpload = () => true,
    onSuccess = () => {},
    onCheckFail = () => {},
    onError = () => {},
    onRemove = () => {},
    onChange = () => {},
    disabled = false,
    defaultImg = '',
    changeValue = () => {},
    version = false,
    settlein = false,
  } = props;

  const [fileValue, setFileValue] = useState(defaultValue || value);
  const first = useRef<Boolean>(true);

  useEffect(() => {
    if (first.current && !fileValue.length) {
      return;
    } else {
      onChange(fileValue);
    }
    if (first.current) {
      first.current = false;
    }
  }, [fileValue]);

  const imageAttribute = (url) =>
    new Promise((resolve) => {
      const image = new Image();
      image.src = url;
      image.onload = () => {
        resolve(image);
      };
    });

  const readBlobAsDataURL = (blob) =>
    new Promise((resolve) => {
      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        resolve(e?.target?.result);
      };
      fileReader.readAsDataURL(blob);
    });

  const _onBeforeUpload = async (file) => {
    // 图片宽高校验
    if (fileWidAndHei.length) {
      return new Promise(async (resolve, reject) => {
        const url = await readBlobAsDataURL(file);
        const { width, height } = (await imageAttribute(url)) as HTMLImageElement;
        if (
          (fileWidAndHei[0] == '*' ? false : width !== fileWidAndHei[0]) ||
          height !== fileWidAndHei[1]
        ) {
          const err = new Error(
            $i18n.get({
              id: 'aopf.Uploader.component.image.TheWidthAndHeightOf',
              dm: '图片宽高不正确',
            }),
          ) as any;
          err.fileName = file.name;
          err.fileType = file.type;
          err.fileWidAndHei = fileWidAndHei;
          err.code = 'SHAPEER';
          onCheckFail(
            err,
            {
              msg: $i18n.get({
                id: 'aopf.Uploader.component.image.TheWidthAndHeightOf',
                dm: '图片宽高不正确',
              }),
            },
            file,
          );
          return reject(false);
        } else {
          return resolve(beforeUpload(file));
        }
      });
    }

    // 图片尺寸校验
    if (maxSize) {
      return new Promise(async (resolve, reject) => {
        if (file.size > maxSize) {
          const unitMaxSize = getFileSize(maxSize);
          const err = new Error(
            $i18n.get(
              {
                id: 'aopf.Uploader.component.image.FileSizeExceedsUnitmaxsizeLimit',
                dm: '文件尺寸超出{unitMaxSize}限制',
              },
              { unitMaxSize: unitMaxSize },
            ),
          ) as any;
          err.fileName = file.name;
          err.fileSize = file.size;
          err.maxSize = maxSize;
          err.code = 'SIZEERR';
          onCheckFail(
            err,
            {
              msg: $i18n.get(
                {
                  id: 'aopf.Uploader.component.image.FileSizeExceedsUnitmaxsizeLimit',
                  dm: '文件尺寸超出{unitMaxSize}限制',
                },
                { unitMaxSize: unitMaxSize },
              ),
            },
            file,
          );
          return reject(false);
        } else {
          return resolve(beforeUpload(file));
        }
      });
    }

    return beforeUpload(file);
  };

  const _onSuccess = (formattedData, file) => {
    const _value = {
      ...(formattedData.originData ||
        (formattedData.response && formattedData.response.originData) ||
        (formattedData.response && formattedData.response.data) ||
        {}),
    };
    if (fileValue.length === 0) {
      _value.order = 0;
    } else {
      _value.order = fileValue[fileValue.length - 1].order + 1;
    }

    const _newValue = [_value];
    setFileValue(_newValue);
    onSuccess(value, formattedData, file);
    if (formattedData?.response?.originData) {
      changeValue({
        publicUrl: formattedData.response.originData.publicUrl,
        relativePath: formattedData.response.originData.relativePath,
      });
    } else {
      changeValue({
        publicUrl: formattedData.response.data.publicUrl,
        relativePath: formattedData.response.data.relativePath,
      });
    }
  };

  const _onError = (e) => {
    let err: any = {};
    if (e.error.code === 'BEFOREUPLOAD_REJECT' && e.error.message === 'BEFOREUPLOAD_REJECT') {
      return false;
    } else {
      err = new Error(
        $i18n.get({ id: 'aopf.Uploader.component.image.SystemException', dm: '系统异常' }),
      );
      err.fileName = e.name;
      err.fileSize = e.size;
      err.fileType = e.type;
      err.code = 'SYSERR';
      err.unknown = true;
      return onError(err, e.response, e);
    }
  };

  const _onRemove = (file) => {
    const _value = fileValue.filter(
      (v) =>
        !(
          v.downloadURL === file.downloadURL ||
          v.fileUrl === file.downloadURL ||
          v.url === file.url
        ),
    );
    setFileValue(_value);
    onRemove(file);
    changeValue({ publicUrl: '', relativePath: '' });
  };

  const newProps = propsFilter(props, ['onChange']);

  const accepts = Array.isArray(accept) ? accept.join(',') : accept;
  const extraData = Object.assign({}, data);
  return (
    <div>
      {value?.length === 0 && !settlein ? (
        <Upload
          onSuccess={_onSuccess}
          action={version ? '/rest/intercnn/app/uploadIcon' : '/rest/intercnn/uploadPublicRead'}
          accept={accepts}
          data={version && extraData}
          beforeUpload={_onBeforeUpload}
          onError={_onError}
        >
          <img style={{ width: '100px', height: '100px' }} src={defaultImg} />
        </Upload>
      ) : (
        <Upload.Card
          {...newProps.other}
          disabled={disabled}
          action={action}
          reUpload={!disabled}
          showDownload={false}
          listType={'card'}
          accept={accepts}
          data={extraData}
          value={transformer(fileValue)}
          formatter={newProps.formatter || formatter}
          beforeUpload={_onBeforeUpload}
          onSuccess={_onSuccess}
          onError={_onError}
          onRemove={_onRemove}
          limit={1}
        >
          {newProps.children}
        </Upload.Card>
      )}
    </div>
  );
};

export default ImageUploader;
